import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonContent,
  IonItem,
  IonLabel,
  IonInput,
  IonCheckbox,
  IonToggle,
  IonSelect,
  IonSelectOption,
  IonCard,
  IonCardContent,
  IonSpinner,
  IonText,
  IonAlert,
} from '@ionic/react';
import { DAY_LABELS } from '../../domain/Irrigation';
import { useIrrigationScheduleForm } from '../../application/use-cases/irrigation/useIrrigationScheduleForm';
import DeleteRow from '../components/DeleteRow';
import './IrrigationSchedule.css';

const IrrigationSchedule: React.FC = () => {
  const { id, channel: channelParam } = useParams<{ id: string; channel: string }>();
  const channel = Number(channelParam) || 1;
  const history = useHistory();
  const {
    device,
    valveName,
    soilProbes,
    form,
    hasSchedule,
    loading,
    saving,
    error,
    saved,
    updateForm,
    toggleDay,
    handleSave,
    handleDelete,
  } = useIrrigationScheduleForm(id, channel);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/app/devices/${id}`} />
          </IonButtons>
          <IonTitle>Horario de riego</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}

        {!loading && (
          <>
            <IonText color="medium">
              <p style={{ fontSize: 13, marginTop: 0 }}>
                {valveName} · {device?.name}
              </p>
            </IonText>

            <IonCard>
              <IonCardContent>
                <IonItem lines="none" style={{ '--padding-start': 0 } as React.CSSProperties}>
                  <IonToggle checked={form.enabled} onIonChange={(e) => updateForm({ enabled: e.detail.checked })}>
                    Horario activo
                  </IonToggle>
                </IonItem>
                {!form.enabled && (
                  <IonText color="medium">
                    <p style={{ fontSize: 12.5, margin: '0 2px 4px' }}>
                      Pausado: se guarda la configuración pero la válvula no riega sola hasta que la reactives.
                    </p>
                  </IonText>
                )}
              </IonCardContent>
            </IonCard>

            <IonCard>
              <IonCardContent>
                <p className="irrigation-form-section-title">Días de la semana</p>
                <div className="irrigation-days-row">
                  {DAY_LABELS.map((label, day) => (
                    <label key={day} className={`irrigation-day-chip ${form.daysOfWeek.includes(day) ? 'selected' : ''}`}>
                      <IonCheckbox checked={form.daysOfWeek.includes(day)} onIonChange={() => toggleDay(day)} />
                      {label}
                    </label>
                  ))}
                </div>

                <IonItem>
                  <IonLabel position="stacked">Hora de inicio</IonLabel>
                  <IonInput
                    type="time"
                    value={form.startTime}
                    onIonInput={(e) => updateForm({ startTime: e.detail.value ?? form.startTime })}
                  />
                </IonItem>

                <IonItem>
                  <IonLabel position="stacked">Duración (minutos)</IonLabel>
                  <IonInput
                    type="number"
                    value={form.durationMinutes}
                    onIonInput={(e) => updateForm({ durationMinutes: Number(e.detail.value) || 0 })}
                  />
                </IonItem>

                <IonItem>
                  <IonLabel position="stacked">Caudal (litros por minuto)</IonLabel>
                  <IonInput
                    type="number"
                    value={form.flowRateLpm}
                    onIonInput={(e) => updateForm({ flowRateLpm: Number(e.detail.value) || 0 })}
                  />
                </IonItem>
              </IonCardContent>
            </IonCard>

            <IonCard>
              <IonCardContent>
                <p className="irrigation-form-section-title">Clima</p>
                <IonItem lines="none">
                  <IonToggle
                    checked={form.rainSkipEnabled}
                    onIonChange={(e) => updateForm({ rainSkipEnabled: e.detail.checked })}
                  >
                    No regar si pronostica lluvia
                  </IonToggle>
                </IonItem>
                {form.rainSkipEnabled && (
                  <IonItem>
                    <IonLabel position="stacked">Saltear si la probabilidad de lluvia es igual o mayor a (%)</IonLabel>
                    <IonInput
                      type="number"
                      value={form.rainSkipProbabilityPct}
                      onIonInput={(e) => updateForm({ rainSkipProbabilityPct: Number(e.detail.value) || 0 })}
                    />
                  </IonItem>
                )}
                <IonText color="medium">
                  <p style={{ fontSize: 12.5, margin: '0 2px 0' }}>
                    ¿Querés que te avise cuando se saltea un riego programado? Activalo en Ajustes → Alertas.
                  </p>
                </IonText>
              </IonCardContent>
            </IonCard>

            <IonCard>
              <IonCardContent>
                <p className="irrigation-form-section-title">Sonda de suelo (opcional)</p>
                <IonItem lines={form.humidityDeviceId ? undefined : 'none'}>
                  <IonSelect
                    label="Vincular sonda"
                    placeholder="Sin sonda vinculada"
                    value={form.humidityDeviceId ? `${form.humidityDeviceId}:${form.humidityChannel ?? 1}` : ''}
                    onIonChange={(e) => {
                      const [deviceId, ch] = String(e.detail.value || '').split(':');
                      updateForm({ humidityDeviceId: deviceId || null, humidityChannel: deviceId ? Number(ch) : null });
                    }}
                  >
                    <IonSelectOption value="">Sin sonda vinculada</IonSelectOption>
                    {soilProbes.map((p) => (
                      <IonSelectOption key={`${p.deviceId}:${p.channel}`} value={`${p.deviceId}:${p.channel}`}>
                        {p.label}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
                {soilProbes.length === 0 && (
                  <IonText color="medium">
                    <p style={{ fontSize: 12.5, margin: '4px 2px 0' }}>
                      Ninguna de tus estaciones tiene sondas de suelo. Agregá una en la estación para no regar si el suelo ya está húmedo.
                    </p>
                  </IonText>
                )}
                {form.humidityDeviceId && (
                  <IonItem>
                    <IonLabel position="stacked">Saltear si la humedad de suelo es igual o mayor a (%)</IonLabel>
                    <IonInput
                      type="number"
                      value={form.humiditySkipThresholdPct ?? 50}
                      onIonInput={(e) => updateForm({ humiditySkipThresholdPct: Number(e.detail.value) || 0 })}
                    />
                  </IonItem>
                )}
              </IonCardContent>
            </IonCard>

            {error && (
              <IonText color="danger">
                <p style={{ fontSize: 13 }}>{error}</p>
              </IonText>
            )}
            {saved && (
              <IonText color="success">
                <p style={{ fontSize: 13 }}>Horario guardado.</p>
              </IonText>
            )}

            <IonButton
              expand="block"
              disabled={saving}
              onClick={async () => {
                const success = await handleSave();
                if (success) history.push(`/app/devices/${id}`);
              }}
            >
              Guardar horario
            </IonButton>
            {hasSchedule && (
              <DeleteRow
                title="Eliminar horario"
                hint="La válvula deja de regar sola en estos días"
                disabled={saving}
                onClick={() => setShowDeleteConfirm(true)}
              />
            )}
          </>
        )}

        <IonAlert
          isOpen={showDeleteConfirm}
          onDidDismiss={() => setShowDeleteConfirm(false)}
          header="¿Eliminar el horario de riego?"
          message="La válvula deja de regar sola. Podés volver a configurarlo cuando quieras."
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Eliminar', role: 'destructive', handler: handleDelete },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default IrrigationSchedule;
